// ===================================================
// sessionApi.js — Edge Function 호출 래퍼 (프론트엔드용)
//
// 역할:
//   기존에 브라우저에서 프롬프트를 조립하고 /api/gpt를 호출하던 로직을
//   서버 측 Edge Function(/api/generate-questions 등)으로 이전했습니다.
//   이 파일은 Edge Function을 호출하는 얇은 래퍼만 남아 있습니다.
//
// 프롬프트 내용(prompts.js)은 더 이상 브라우저에 포함되지 않습니다.
// ===================================================

import { getAuthToken } from './gptClient';

// ── 공통 fetch 유틸 ──────────────────────────────────────

async function callEdge(url, body) {
  const token = await getAuthToken();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || '일시적인 오류가 발생했어요.');
  }
  const { content, error } = await res.json();
  if (error) throw new Error(error);
  return content;
}

async function callEdgeStream(url, body, onChunk) {
  const token = await getAuthToken();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || '일시적인 오류가 발생했어요.');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    accumulated += decoder.decode(value, { stream: true });
    if (onChunk) onChunk(accumulated);
  }
  return accumulated;
}

// ── 질문 생성 ──────────────────────────────────────────

export async function askGPT(userText, mode, pastContext = null, preferenceSummary = null, lifeSummary = null, onChunk = null) {
  const body = { text: userText, mode, pastContext, preferenceSummary, lifeSummary };

  // 모든 모드에서 스트리밍 사용 (의사결정 모드 포함 — 체감 속도 향상)
  return callEdgeStream('/api/generate-questions', body, onChunk);
}

export async function continueGPT(initialText, allRounds, mode, pastContext = null, preferenceSummary = null, lifeSummary = null, discussedAxes = [], previousQuestions = [], onChunk = null, { round, avgAnswerLen, lastAnswerLen } = {}) {
  const body = {
    initialText, allRounds, mode, pastContext, preferenceSummary, lifeSummary,
    discussedAxes, previousQuestions,
    round, avgAnswerLen, lastAnswerLen,
  };

  // 모든 모드에서 스트리밍 사용
  return callEdgeStream('/api/continue-questions', body, onChunk);
}

export async function regenerateSingleQuestion(userText, mode, otherQuestion, preferenceSummary = null, previousQuestions = []) {
  return callEdge('/api/regenerate-question', { userText, mode, otherQuestion, preferenceSummary, previousQuestions });
}

// ── 키워드/요약/메타 (save-session Edge Function에 통합됨) ──
// 개별 함수는 호환성을 위해 남겨두지만, save-session에서 일괄 처리합니다.

export async function extractKeywords(initialText, rounds) {
  // save-session Edge Function 내부에서 처리됨
  // handleFreewriteSave 등에서 단독 호출될 수 있으므로 유지
  return callEdge('/api/ai-utils', {
    action: 'extractKeywords',
    initialText, rounds,
  });
}

export async function resolveKeywordsWithExisting(newKeywords, existingKeywords) {
  if (!existingKeywords || existingKeywords.length === 0) return newKeywords;
  const existing = existingKeywords.slice(0, 80);
  const existingLower = existing.map(k => k.toLowerCase().trim());
  const preResolved = newKeywords.map(nk => {
    const idx = existingLower.indexOf(nk.toLowerCase().trim());
    return idx !== -1 ? existing[idx] : null;
  });
  const needsGPT = preResolved.some(r => r === null);
  if (!needsGPT) return preResolved;
  // GPT 필요한 경우는 드물고, save-session에서 처리됨
  return newKeywords;
}

export async function summarizeTitle(initialText, rounds) {
  // save-session에서 처리됨 — 단독 호출 시 폴백
  return null;
}

export async function summarizeContext(initialText, rounds) {
  // save-session에서 처리됨 — 단독 호출 시 폴백
  return null;
}

export async function extractDecisionMeta(initialText, rounds) {
  // save-session에서 처리됨 — 단독 호출 시 폴백
  return null;
}

// ── 선호도/유사 키워드/주간 회고 ──────────────────────

export async function generatePreferenceSummary(acceptedQuestions, rejectedQuestions, deepAnswerQuestions = [], resonantAxes = []) {
  return callEdge('/api/ai-utils', {
    action: 'preferenceSummary',
    acceptedQuestions, rejectedQuestions, deepAnswerQuestions, resonantAxes,
  });
}

export async function findSimilarKeywords(query, existingKeywords) {
  if (!existingKeywords || existingKeywords.length === 0) return [];
  const result = await callEdge('/api/ai-utils', {
    action: 'findSimilarKeywords', query, existingKeywords,
  });
  const trimmed = result.trim();
  if (!trimmed) return [];
  return trimmed.split(',').map(k => k.trim()).filter(k => k && existingKeywords.includes(k));
}

export async function generateWeeklyReview(weekSessions) {
  return callEdge('/api/ai-utils', { action: 'weeklyReview', weekSessions });
}

export async function generateLifeSummaryUpdate(existingSummary, initialText, rounds) {
  return callEdge('/api/ai-utils', { action: 'lifeSummaryUpdate', existingSummary, initialText, rounds });
}
